import { isValidEmail, isValidUrl } from '@/lib/utils';

// XSS Prevention - Sanitize HTML content
export const sanitizeHtml = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  // Remove dangerous HTML tags and attributes
  const dangerousTags = /<script[^>]*>.*?<\/script>/gi;
  const dangerousAttributes = /on\w+\s*=\s*["'][^"']*["']/gi;
  const dangerousProtocols = /javascript:|data:|vbscript:/gi;
  
  return input
    .replace(dangerousTags, '')
    .replace(dangerousAttributes, '')
    .replace(dangerousProtocols, '');
};

// Input Sanitization
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove basic HTML
    .slice(0, 1000); // Limit length
};

// URL Validation and Sanitization
export const sanitizeUrl = (url: string): string | null => {
  if (!url || typeof url !== 'string') return null;
  
  const sanitized = url.trim();
  
  // Check for dangerous protocols
  if (/^(javascript|data|vbscript):/i.test(sanitized)) {
    return null;
  }
  
  // Add https if no protocol
  const finalUrl = sanitized.match(/^https?:\/\//) ? sanitized : `https://${sanitized}`;
  
  return isValidUrl(finalUrl) ? finalUrl : null;
};

// Password Security Validation
export const validatePasswordSecurity = (password: string): {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
} => {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  // Common password check
  const commonPasswords = ['password', '123456', 'qwerty', 'admin', 'letmein'];
  if (commonPasswords.includes(password.toLowerCase())) {
    errors.push('Password is too common');
  }
  
  const strength = errors.length === 0 ? 'strong' : errors.length <= 2 ? 'medium' : 'weak';
  
  return {
    isValid: errors.length === 0,
    errors,
    strength
  };
};

// Email Validation with Security
export const validateEmailSecurity = (email: string): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];
  
  if (!email) {
    errors.push('Email is required');
  } else if (!isValidEmail(email)) {
    errors.push('Please enter a valid email address');
  } else if (email.length > 320) {
    errors.push('Email address is too long');
  }
  
  // Check for suspicious patterns
  if (email.includes('..') || email.startsWith('.') || email.endsWith('.')) {
    errors.push('Email address format is invalid');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// File Upload Security
export const validateFileUpload = (file: File): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
  
  if (file.size > maxSize) {
    errors.push('File size must be less than 5MB');
  }
  
  if (!allowedTypes.includes(file.type)) {
    errors.push('Only JPEG, PNG, WebP, and GIF files are allowed');
  }
  
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
  if (!hasValidExtension) {
    errors.push('File must have a valid image extension');
  }
  
  // Check for executable disguised as image
  if (fileName.includes('.exe') || fileName.includes('.js') || fileName.includes('.php')) {
    errors.push('File type is not allowed');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Content Security Helpers
export const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

export const unescapeHtml = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

// Rate Limiting Helper
export const createRateLimiter = (maxRequests: number, windowMs: number) => {
  const requests: number[] = [];
  
  return {
    isAllowed: (): boolean => {
      const now = Date.now();
      
      // Remove old requests outside the window
      while (requests.length > 0 && now - requests[0] > windowMs) {
        requests.shift();
      }
      
      if (requests.length >= maxRequests) {
        return false;
      }
      
      requests.push(now);
      return true;
    },
    
    getRemainingRequests: (): number => {
      const now = Date.now();
      
      // Remove old requests
      while (requests.length > 0 && now - requests[0] > windowMs) {
        requests.shift();
      }
      
      return Math.max(0, maxRequests - requests.length);
    },
    
    getResetTime: (): number => {
      if (requests.length === 0) return 0;
      return requests[0] + windowMs;
    }
  };
};

// Secure Token Generation (for client-side use only)
export const generateSecureToken = (length: number = 32): string => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Secure Storage Key Generation
export const generateStorageKey = (prefix: string, userId: string): string => {
  const sanitizedPrefix = sanitizeInput(prefix);
  const sanitizedUserId = sanitizeInput(userId);
  return `${sanitizedPrefix}_${sanitizedUserId}`;
};